import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BidFieldPlan, FieldPlanSource } from '../../../_models/field-plan';
import { BidService } from '../bid.service';
import { TrackerCloneChoice } from '../bid-clone';
import { FieldPlanImport, rebuiltStart } from '../bid-field-plan';
import { inRenewalWindow } from '../bid-tracker';

let nextId = 0;

/** Which project the imports read: a next wave reads the tracker's most recent wave, a copy the cloned
 *  bid's own project. A renewal doesn't import. */
type PlanLookup = 'lastWave' | 'own';

/**
 * The clone questions: what a tracker bid's clone becomes, and where the field plan comes from. Shared
 * by the source bid's Clone popup and the Add Bid dialog; each host supplies its own heading and
 * buttons.
 *
 * The field plan imports read how a NAV project ran (`GET bid/{id}/field-plan`), so they're offered only
 * once that project is found; until then, or without one, only the bid's own field plan can be chosen.
 * Once found, "how it started" is selected unless the user has already picked a source: closure's
 * blended CPIs carry fees and in-field raises, so starting from how it ended erodes margin wave on wave.
 */
@Component({
  selector: 'app-bid-tracker-clone-choice',
  templateUrl: './bid-tracker-clone-choice.component.html',
  standalone: false,
})
export class BidTrackerCloneChoiceComponent implements OnChanges, OnDestroy {
  /** Number of the bid being cloned, named in the field plan option. */
  @Input({ required: true }) sourceNumber!: string;
  /** Id of the bid being cloned; the imports read its NAV project (or its tracker's last wave). */
  @Input() sourceBidId: string | null = null;
  /** Whether the bid being cloned was Closed Won and made NAV projects. A copy of one that wasn't has no
   *  project to import from, so it isn't looked up. */
  @Input() sourceWon = false;
  /** The source bid's tracker ID; with one, a wave or renewal offers its bid vs actual comparison. */
  @Input() sourceTrackerId: string | null = null;
  /** False for a non-tracker bid: only the field plan question is asked. */
  @Input() showCloneType = true;
  @Input() cloneType: TrackerCloneChoice = 'separate';
  @Output() cloneTypeChange = new EventEmitter<TrackerCloneChoice>();
  /** The import to apply to the draft, or null for the bid's own field plan. */
  @Output() fieldPlanChange = new EventEmitter<FieldPlanImport | null>();

  fieldPlanSource: FieldPlanSource = 'bid';
  /** Set once the user picks a source, so a project found later doesn't override their choice. */
  private sourcePicked = false;
  /** Per lookup: the project found, null when there's none, undefined while loading. */
  plans: Partial<Record<PlanLookup, BidFieldPlan | null>> = {};
  /** Lookups whose request failed (as opposed to finding no project). */
  failed: Partial<Record<PlanLookup, boolean>> = {};

  // Radio ids and group names are per instance, so two uses never share a group.
  readonly idPrefix = `trackerClone${nextId++}`;
  private destroy$ = new Subject<void>();

  constructor(private bidService: BidService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sourceBidId']) {
      this.plans = {};
      this.failed = {};
      this.sourcePicked = false;
      this.setSource('bid');
    }
    this.loadPlan();
  }

  /** In the renewal window a tracker's clone can't be its next wave. */
  readonly waveLocked = inRenewalWindow();

  /** A renewal copies last year's full year, so it doesn't ask where the field plan comes from. */
  get asksFieldPlan(): boolean {
    return !(this.showCloneType && this.cloneType === 'renewal');
  }

  get lookup(): PlanLookup {
    return this.showCloneType && this.cloneType !== 'separate' ? 'lastWave' : 'own';
  }

  get plan(): BidFieldPlan | null | undefined {
    return this.plans[this.lookup];
  }

  /** "the last wave" for a wave or renewal, "the project" for a copy. */
  get planSubject(): string {
    return this.lookup === 'lastWave' ? 'the last wave' : 'the project';
  }

  /** NAV number, wave and whether the figures are final, under the import options. */
  get planDetail(): string {
    const plan = this.plan;
    if (this.failed[this.lookup]) return "The NAV project's figures couldn't be loaded. Close this and try again.";
    if (plan === undefined) return 'Looking for the NAV project…';
    if (this.lookup === 'own' && !this.sourceWon) return `Not available until bid #${this.sourceNumber} is Closed Won.`;
    if (plan === null) return this.lookup === 'lastWave'
      ? 'Not available: the tracker has no NAV project yet.'
      : 'Not available: this bid has no NAV project.';
    const wave = plan.waveNumber ? `, wave ${plan.waveNumber}` : '';
    const status = plan.vendorClosedAt ? 'closed' : 'still in field, so the figures may change';
    return `From ${plan.projectCode}${wave} (${status}).`;
  }

  /** Whether "how it started" falls back to resizing the booked mix for any country, because the
   *  set-up at the first complete couldn't be rebuilt (no complete yet, or several segments). */
  get startedResized(): boolean {
    return !!this.plan?.projects.some(p => !rebuiltStart(p));
  }

  choose(type: TrackerCloneChoice): void {
    this.cloneType = type;
    this.cloneTypeChange.emit(type);
    // The other lookup reads a different project, so an import picked for it doesn't carry over.
    if (this.fieldPlanSource !== 'bid' && !this.plan) this.setSource('bid');
    else if (!this.sourcePicked && this.plan) this.setSource('started');
    else this.emitImport();
    this.loadPlan();
  }

  /** A source the user selected; it's kept from then on. */
  pick(source: FieldPlanSource): void {
    this.sourcePicked = true;
    this.setSource(source);
  }

  private setSource(source: FieldPlanSource): void {
    this.fieldPlanSource = source;
    this.emitImport();
  }

  private emitImport(): void {
    const plan = this.plan;
    this.fieldPlanChange.emit(this.asksFieldPlan && this.fieldPlanSource !== 'bid' && plan ? { how: this.fieldPlanSource, plan } : null);
  }

  private loadPlan(): void {
    const lookup = this.lookup;
    if (!this.sourceBidId || !this.asksFieldPlan || lookup in this.plans) return;
    if (lookup === 'own' && !this.sourceWon) {
      this.plans = { ...this.plans, [lookup]: null };
      return;
    }
    this.plans = { ...this.plans, [lookup]: undefined };
    const forClone = lookup === 'lastWave' ? 'wave' : 'copy';
    this.bidService.getFieldPlan(this.sourceBidId, forClone).pipe(takeUntil(this.destroy$)).subscribe({
      // No body (204) means there's no NAV project to import from.
      next: plan => {
        this.plans = { ...this.plans, [lookup]: plan ?? null };
        if (plan && lookup === this.lookup && !this.sourcePicked) this.setSource('started');
      },
      error: () => {
        this.plans = { ...this.plans, [lookup]: null };
        this.failed = { ...this.failed, [lookup]: true };
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
